/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリ実行フック。ジョブ一覧取得と実行の TanStack Query ラッパー
 * @graph-connects dataViewerApi [calls] クエリ実行 API クライアント
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */

import { useQuery, useMutation, useQueryClient, skipToken } from '@tanstack/react-query';
import { dataViewerApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  CreateDataViewerJobRequest,
  UpdateDataViewerJobRequest,
  PreviewJobQueryRequest,
  FilterConditionInput,
  RawFilterConditionInput,
  PreviewQueryTablesRequest,
  PreviewTableColumnsRequest,
} from '../types/api.js';

/**
 * クエリジョブ一覧取得フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 実行可能なクエリジョブのメタ情報を取得する TanStack Query フック
 * @graph-connects dataViewerApi [calls] via:getJobs ジョブ一覧取得
 */
export function useDataViewerJobs() {
  return useQuery({
    queryKey: queryKeys.dataViewer.jobs(),
    queryFn: dataViewerApi.getJobs,
    staleTime: 60_000,
  });
}

/**
 * クエリジョブ実行ミューテーションフック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 指定ジョブをフィルタ条件付きで実行するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:runJob クエリ実行
 */
export function useRunQueryJob() {
  return useMutation({
    mutationFn: ({
      jobId,
      filters,
      rawFilters,
    }: {
      jobId: string;
      filters?: FilterConditionInput[];
      rawFilters?: RawFilterConditionInput[];
    }) => dataViewerApi.runJob(jobId, filters, rawFilters),
  });
}

/**
 * CSVエクスポート用クエリ実行ミューテーションフック
 *
 * 実行フックと同じくミューテーションにするのは、エクスポートも実行と同じくフィルタ条件を
 * API へ送り、カラム名 allowlist 違反や数値として解釈できない値で `ValidationError` を
 * 受け取りうるため。素の `await` で呼ぶと reject が未捕捉になり画面に何も出ない。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 指定ジョブをフィルタ条件付きで実行し CSV 出力用の全行を取得するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:exportJob CSVエクスポート用クエリ実行
 */
export function useExportQueryJob() {
  return useMutation({
    mutationFn: ({
      jobId,
      filters,
      rawFilters,
    }: {
      jobId: string;
      filters?: FilterConditionInput[];
      rawFilters?: RawFilterConditionInput[];
    }) => dataViewerApi.exportJob(jobId, filters, rawFilters),
  });
}

/**
 * クエリジョブ登録ミューテーションフック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリジョブを Firestore に登録するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:createJob ジョブ登録
 */
export function useCreateDataViewerJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDataViewerJobRequest) => dataViewerApi.createJob(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataViewer.jobs() });
    },
  });
}

/**
 * クエリプレビュー実行ミューテーションフック
 *
 * 登録/更新画面が filterableColumns の妥当性（実データに対して整合するか）を送信前に検証する
 * ため、SQL を試験実行（maxRows: 50）して結果カラム・サンプル行を取得する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリ登録/更新前に SQL を試験実行するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:previewJobQuery クエリプレビュー実行
 */
export function usePreviewJobQuery() {
  return useMutation({
    mutationFn: (input: PreviewJobQueryRequest) => dataViewerApi.previewJobQuery(input),
  });
}

/**
 * クエリジョブ詳細取得フック（SQL・権限情報含む）
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business ジョブ詳細（SQL・権限含む）を取得する TanStack Query フック
 * @graph-connects dataViewerApi [calls] via:getJobDetail ジョブ詳細取得
 */
export function useDataViewerJobDetail(id: string | null) {
  return useQuery({
    queryKey: [...queryKeys.dataViewer.jobs(), 'detail', id],
    queryFn: id !== null ? () => dataViewerApi.getJobDetail(id) : skipToken,
    staleTime: 30_000,
  });
}

/**
 * クエリジョブ更新ミューテーションフック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 既存クエリジョブを更新するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:updateJob ジョブ更新
 */
export function useUpdateDataViewerJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDataViewerJobRequest }) =>
      dataViewerApi.updateJob(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataViewer.jobs() });
    },
  });
}

/**
 * アクセス可能 DB 名一覧取得フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business ログインユーザーがアクセスできる DB 名一覧を取得する TanStack Query フック
 * @graph-connects dataViewerApi [calls] via:getDatabases DB名一覧取得
 */
export function useDatabaseList() {
  return useQuery({
    queryKey: queryKeys.dataViewer.databases(),
    queryFn: () => dataViewerApi.getDatabases().then((res) => res.databases),
    staleTime: 300_000,
  });
}

/**
 * クエリジョブ削除ミューテーションフック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリジョブを Firestore から削除するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:deleteJob ジョブ削除
 */
export function useDeleteDataViewerJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dataViewerApi.deleteJob(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataViewer.jobs() });
    },
  });
}

/**
 * 保存前の SQL が参照しているテーブル一覧を取得するミューテーションフック
 *
 * 登録画面がフィルタ対象カラムを選ばせるために使う。ジョブはまだ存在しないので、
 * SQL と接続先を直接渡す。SQL は編集途中で何度も変わるためクエリではなくミューテーションにする。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 登録画面でフィルタ対象テーブルの候補を取得するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:previewQueryTables 参照テーブル一覧取得
 */
export function usePreviewQueryTables() {
  return useMutation({
    mutationFn: (input: PreviewQueryTablesRequest) => dataViewerApi.previewQueryTables(input),
  });
}

/**
 * 保存前の SQL に対して、テーブルのカラム一覧を取得するミューテーションフック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 登録画面でフィルタ対象カラムの候補と型を取得するミューテーションフック
 * @graph-connects dataViewerApi [calls] via:previewTableColumns カラム一覧取得
 */
export function usePreviewTableColumns() {
  return useMutation({
    mutationFn: (input: PreviewTableColumnsRequest) => dataViewerApi.previewTableColumns(input),
  });
}
